import React, { useState, useEffect } from 'react';
import './styles/ProgressionApp.css';
import './styles/LoginForm.css';
import ExerciseList from './components/ProgressionUI/ExerciseList';
import ExerciseProgressionChart from './components/ProgressionUI/ExerciseProgressionChart';
import LoginForm from './components/LoginForm';
import { workoutPrograms } from './data/workoutProgram';
import { api } from './services/api';

// Get all unique exercises from all workout programs
const getAllExercises = () => {
  const exercises = new Set();
  workoutPrograms.forEach(program => {
    program.exercises.forEach(exercise => {
      exercises.add(exercise.name);
    });
  });
  return Array.from(exercises);
};

function ProgressionApp() {
  const [exercises, setExercises] = useState([]);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [hoveredExercise, setHoveredExercise] = useState(null);
  const [exerciseHistory, setExerciseHistory] = useState([]);
  const [hoveredExerciseHistory, setHoveredExerciseHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check authentication on load
  useEffect(() => {
    const checkAuth = async () => {
      if (api.isAuthenticated()) {
        try {
          // Try to fetch data to verify the token is still valid
          await api.getWorkouts();
          setIsAuthenticated(true);
        } catch (error) {
          // If there's an error, the token might be invalid
          api.logout();
        }
      }
      setIsLoading(false);
    };
    
    checkAuth();
  }, []);

  // Load all exercises on initial render and select the first one
  useEffect(() => {
    if (isAuthenticated) {
      const allExercises = getAllExercises();
      setExercises(allExercises);
      if (allExercises.length > 0) {
        setSelectedExercise(allExercises[0]);
      }
    }
  }, [isAuthenticated]);

  // Load exercise history when an exercise is selected
  useEffect(() => {
    if (selectedExercise) {
      setLoading(true);
      api.getExerciseHistory(selectedExercise)
        .then(history => {
          setExerciseHistory(history);
          setLoading(false);
        })
        .catch(error => {
          console.error(`Error fetching history for ${selectedExercise}:`, error);
          setExerciseHistory([]);
          setLoading(false);
        });
    } else {
      setExerciseHistory([]);
    }
  }, [selectedExercise]);

  // Load hovered exercise history
  useEffect(() => {
    if (hoveredExercise && hoveredExercise !== selectedExercise) {
      api.getExerciseHistory(hoveredExercise)
        .then(history => {
          setHoveredExerciseHistory(history);
        })
        .catch(error => {
          console.error(`Error fetching history for ${hoveredExercise}:`, error);
          setHoveredExerciseHistory([]);
        });
    }
  }, [hoveredExercise, selectedExercise]);

  const handleExerciseSelect = (exerciseName) => {
    setSelectedExercise(exerciseName);
  };

  const handleExerciseHover = (exerciseName) => {
    setHoveredExercise(exerciseName);
  };

  const handleHoverEnd = () => {
    setHoveredExercise(null);
  };

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
  };
  
  const handleLogout = () => {
    api.logout();
    setIsAuthenticated(false);
  };
  
  // Determine which exercise to display in the chart
  const displayExercise = hoveredExercise || selectedExercise;
  const displayHistory = hoveredExercise ? hoveredExerciseHistory : exerciseHistory;
  
  if (isLoading) {
    return <div className="loading-screen">Loading...</div>;
  }
  
  if (!isAuthenticated) {
    return <LoginForm onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="progression-app">
      <div className="main-container">
        <main>
          <div className="progression-container">
            <ExerciseList 
              exercises={exercises} 
              selectedExercise={selectedExercise}
              onSelectExercise={handleExerciseSelect}
              onHoverExercise={handleExerciseHover}
              onHoverEnd={handleHoverEnd}
            />
            <div className={`chart-container ${hoveredExercise ? 'hovered' : ''}`}>
              {displayExercise ? (
                loading && !hoveredExercise ? (
                  <div className="loading">Loading data...</div>
                ) : (
                  <ExerciseProgressionChart 
                    exercise={displayExercise}
                    history={displayHistory}
                  />
                )
              ) : (
                <div className="no-selection">
                  <p>Select an exercise to view progression</p>
                </div>
              )}
            </div>
          </div>
        </main>
        <button onClick={handleLogout} className="logout-button">Logout</button>
      </div>
    </div>
  );
}

export default ProgressionApp;